import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { RETENTION_POLICIES } from '../api/types';
import type { RetentionPolicy } from '../api/types';
import { usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatDate, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Button, Card } from '../components/ui';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { RetentionBadge } from './shared';
import { IconPlus, IconDownload, IconClose } from '../components/Icons';

const PAGE = 20;

export function Archives() {
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const list = usePaginated((cursor) => api.listArchives(PAGE, cursor), []);

  return (
    <>
      <PageHeader
        title={t('archives.title')}
        subtitle={t('archives.subtitle')}
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            <IconPlus width={18} height={18} />
            {t('archives.create')}
          </Button>
        }
      />

      {creating ? (
        <CreateArchiveDialog
          onClose={() => setCreating(false)}
          onCreated={(name) => {
            setCreating(false);
            toast.success(t('archives.created', { name }));
            list.reload();
          }}
        />
      ) : null}

      <Card>
        <ApiState status={list.status} error={list.error} data={list.status === 'error' ? null : list.items} onRetry={list.reload}>
          {(items) =>
            items.length === 0 ? (
              <EmptyState
                title={t('archives.empty')}
                action={
                  <Button variant="primary" onClick={() => setCreating(true)}>
                    <IconPlus width={18} height={18} />
                    {t('archives.create')}
                  </Button>
                }
              />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t('common.name')}</th>
                        <th className="num">{t('archives.colObjects')}</th>
                        <th className="num">{t('archives.colSize')}</th>
                        <th>{t('archives.colRetention')}</th>
                        <th>{t('common.createdAt')}</th>
                        <th className="num">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((a) => (
                        <tr key={a.id}>
                          <td className="cell-strong">
                            <Link to={`/archives/${a.id}`}>{a.name}</Link>
                          </td>
                          <td className="num">{formatNumber(a.objects, lang)}</td>
                          <td className="num">{formatBytes(a.bytes, lang)}</td>
                          <td>
                            <RetentionBadge policy={a.retentionPolicy} />
                          </td>
                          <td className="muted">{formatDate(a.createdAt, lang)}</td>
                          <td className="num">
                            <a
                              className="btn btn--secondary btn--sm"
                              href={api.dmdUrl(a.id)}
                              title={t('archives.deliverDmdHint')}
                            >
                              <IconDownload width={16} height={16} />
                              {t('archives.deliverDmd')}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onLoadMore={list.loadMore} />
              </>
            )
          }
        </ApiState>
      </Card>
    </>
  );
}

function CreateArchiveDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (name: string) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [policy, setPolicy] = useState<RetentionPolicy>('KEEP_ALL');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('common.required'));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await api.createArchive(name.trim(), policy);
      onCreated(name.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="create-title">
        <div className="row between">
          <h2 className="dialog__title" id="create-title">
            {t('archives.createTitle')}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label={t('archives.nameLabel')} required error={error}>
            {(p) => (
              <input
                className="input"
                {...p}
                value={name}
                autoFocus
                placeholder={t('archives.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('retention.label')}>
            {(p) => (
              <select
                className="select"
                {...p}
                value={policy}
                onChange={(e) => setPolicy(e.target.value as RetentionPolicy)}
              >
                {RETENTION_POLICIES.map((rp) => (
                  <option key={rp} value={rp}>
                    {t(`retention.${rp}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.create')}
          </Button>
        </div>
      </form>
    </div>
  );
}
